import { create } from 'zustand'

interface User {
  id: string
  user_id?: string
  email: string
  username?: string
  full_name?: string
  hotel_id?: string
  is_manager?: boolean
  user_type?: 'guest' | 'hotel_admin'  // Тип пользователя: гость или администратор отеля
}

interface Guest {
  id: number
  telegram_user_id?: number
  hotel_id: string
  full_name?: string
  passport_number?: string
  citizenship?: string
  date_of_birth?: string
  phone?: string
  email?: string
  documents: any[]
  status: 'ACTIVE' | 'COMPLETED' | 'REJECTED'
  session_id?: string
  current_state?: string
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  error: string | null
  
  setUser: (user: User | null) => void
  setToken: (token: string | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('token'),
  isLoading: false,
  error: null,

  setUser: (user) => set({ user }),
  setToken: (token) => {
    if (token) {
      localStorage.setItem('token', token)
    } else {
      localStorage.removeItem('token')
    }
    set({ token })
  },
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  logout: () => {
    localStorage.removeItem('token')
    set({ user: null, token: null })
  },
}))

interface GuestState {
  guest: Guest | null
  guests: Guest[]
  currentState: string | null  // FSM state instead of step number
  stateData: Record<string, any>  // All collected data for current state
  isLoading: boolean
  error: string | null

  setGuest: (guest: Guest | null) => void
  setGuests: (guests: Guest[]) => void
  setCurrentState: (state: string) => void
  setStateData: (data: Record<string, any>) => void
  updateStateData: (data: Record<string, any>) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export const useGuestStore = create<GuestState>((set) => {
  // Восстановить guest из localStorage при загрузке
  const savedGuest = localStorage.getItem('guest')
  const initialGuest = savedGuest ? JSON.parse(savedGuest) : null
  
  return {
    guest: initialGuest,
    guests: [],
    currentState: null,
    stateData: {},
    isLoading: false,
    error: null,

    setGuest: (guest) => {
      if (guest) {
        localStorage.setItem('guest', JSON.stringify(guest))
      } else {
        localStorage.removeItem('guest')
      }
      set({ guest })
    },
    setGuests: (guests) => set({ guests }),
    setCurrentState: (state) => set({ currentState: state }),
    setStateData: (data) => set({ stateData: data }),
    updateStateData: (data) =>
      set((state) => ({
        stateData: { ...state.stateData, ...data },
      })),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
  }
})

interface PaymentState {
  tariffs: any[]
  balance: number
  isLoading: boolean
  error: string | null

  setTariffs: (tariffs: any[]) => void
  setBalance: (balance: number) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export const usePaymentStore = create<PaymentState>((set) => ({
  tariffs: [],
  balance: 0,
  isLoading: false,
  error: null,

  setTariffs: (tariffs) => set({ tariffs }),
  setBalance: (balance) => set({ balance }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}))

interface ReferralState {
  isManager: boolean
  promoCode: string
  stats: {
    level1_referrals: number
    level2_referrals: number
    commission_earned_kopecks: number
    available_payout_kopecks: number
  } | null
  isLoading: boolean
  error: string | null

  setIsManager: (isManager: boolean) => void
  setPromoCode: (code: string) => void
  setStats: (stats: any) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export const useReferralStore = create<ReferralState>((set) => ({
  isManager: false,
  promoCode: '',
  stats: null,
  isLoading: false,
  error: null,

  setIsManager: (isManager) => set({ isManager }),
  setPromoCode: (code) => set({ promoCode: code }),
  setStats: (stats) => set({ stats }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}))

// ==================== HOTEL ADMIN ====================

export interface HotelAdmin {
  id: number
  email: string
  full_name: string
  hotel_name: string
  phone?: string
  is_active: boolean
  verified: boolean
  created_at: string
  subscription_status?: 'active' | 'expired' | 'pending'
  subscription_expires_at?: string
}

export interface HotelAdminStats {
  total_documents: number
  total_guests: number
  documents_this_month: number
  active_subscription: boolean
  subscription_expires_at?: string
}

interface HotelAdminState {
  admin: HotelAdmin | null
  stats: HotelAdminStats | null
  isLoading: boolean
  error: string | null

  setAdmin: (admin: HotelAdmin | null) => void
  setStats: (stats: HotelAdminStats | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  getAccessToken: () => string | null
  logout: () => void
}

export const useHotelAdminStore = create<HotelAdminState>((set, get) => {
  const savedAdmin = localStorage.getItem('hotel_admin')
  const initialAdmin = savedAdmin ? JSON.parse(savedAdmin) : null

  return {
    admin: initialAdmin,
    stats: null,
    isLoading: false,
    error: null,

    setAdmin: (admin) => {
      if (admin) {
        localStorage.setItem('hotel_admin', JSON.stringify(admin))
        // 🔐 Сохраняем токен в localStorage если он есть в admin объекте
        // При логине: access_token из response API сохраняется в localStorage под ключом 'token'
        if ((admin as any).access_token) {
          localStorage.setItem('token', (admin as any).access_token)
        }
      } else {
        localStorage.removeItem('hotel_admin')
        localStorage.removeItem('token')
      }
      set({ admin })
    },
    setStats: (stats) => set({ stats }),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    // 🔑 Получить токен доступа из localStorage
    // Используется для всех API запросов которым нужна авторизация
    getAccessToken: () => {
      return localStorage.getItem('token')
    },
    logout: () => {
      localStorage.removeItem('hotel_admin')
      localStorage.removeItem('token')
      set({ admin: null, stats: null })
    },
  }
})
